import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { X, User, Activity } from 'lucide-react';
import { fmt, fmtDT, StatusPill, Spinner, ErrorBanner, UNAVAILABLE } from './ReportTableKit';

const API = process.env.REACT_APP_BACKEND_URL;

function Stat({ label, value }) {
  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', minWidth: 110 }}>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>{value}</div>
    </div>
  );
}

const parse = (s) => { try { return s ? parseISO(String(s)) : null; } catch { return null; } };

function RecencyBadge({ rows }) {
  const dates = [];
  rows.forEach(r => { [r.completed_at, r.enrolled_at].forEach(d => { const p = parse(d); if (p) dates.push(p); }); });
  if (dates.length === 0) return null;
  const last = new Date(Math.max(...dates.map(d => d.getTime())));
  const days = differenceInCalendarDays(new Date(), last);
  let bg, fg, label;
  if (days <= 30) { bg = '#dcfce7'; fg = '#15803d'; label = 'Active'; }
  else if (days <= 90) { bg = '#fef9c3'; fg = '#a16207'; label = 'Recent'; }
  else { bg = '#f1f5f9'; fg = '#64748b'; label = 'Dormant'; }
  const rel = days <= 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }} data-testid="drawer-recency">
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: bg, color: fg }}>
        <Activity size={13} /> {label}
      </span>
      <span style={{ fontSize: 12, color: '#64748b' }} data-testid="drawer-last-activity">
        Last activity: <strong style={{ color: '#0f172a' }}>{fmtDT(last.toISOString())}</strong> ({rel})
      </span>
    </div>
  );
}

export default function LearnerHistoryDrawer({ lmsId, learnerName, pii, onClose }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const t = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setTimeout(onClose, 220);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  useEffect(() => {
    if (lmsId == null) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await axios.get(`${API}/api/lup/v2/report/course-activity`, {
          params: { search: String(lmsId), per_page: 500, page: 1, sort: 'enrolled_at:desc' },
          withCredentials: true,
        });
        if (cancelled) return;
        const all = r.data?.data || [];
        const mine = all.filter(x => String(x.student_user_id) === String(lmsId));
        setRows(mine);
      } catch (e) {
        if (!cancelled) { setErr(UNAVAILABLE('Learner history')); setRows([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lmsId]);

  const total = rows.length;
  const completed = rows.filter(r => String(r.enrollment_status || '').toLowerCase() === 'completed').length;
  const inProgress = rows.filter(r => String(r.enrollment_status || '').toLowerCase() === 'in progress').length;
  const first = rows[0] || {};
  const displayName = pii ? (learnerName || first.learner_name) : null;

  return (
    <div data-testid="learner-history-drawer" style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
      <div onClick={close} style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)', opacity: open ? 1 : 0, transition: 'opacity 0.2s ease' }} />
      <aside
        style={{
          position: 'absolute', top: 0, right: 0, height: '100%', width: 'min(860px, 92vw)',
          background: '#fff', boxShadow: '-12px 0 40px rgba(15,23,42,0.2)', display: 'flex', flexDirection: 'column',
          transform: open ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.22s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <User size={20} color="#4338ca" />
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }} data-testid="drawer-learner-name">
                {displayName || `Learner #${lmsId}`}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>LMS ID: <span style={{ fontFamily: 'monospace' }}>{lmsId}</span></span>
                {first.organization && <span>· {first.organization}</span>}
                {first.country && <span>· {first.country}</span>}
                {pii && first.email && <span style={{ fontFamily: 'monospace', color: '#3182ce' }}>· {first.email}</span>}
              </div>
            </div>
          </div>
          <button onClick={close} data-testid="drawer-close-btn" aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#64748b' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
          {loading ? <Spinner /> : err ? <ErrorBanner message={err} testId="drawer-error" /> : (
            <>
              <div style={{ marginBottom: 16 }}><RecencyBadge rows={rows} /></div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                <Stat label="Enrollments" value={fmt(total)} />
                <Stat label="Completed" value={fmt(completed)} />
                <Stat label="In Progress" value={fmt(inProgress)} />
              </div>
              {total === 0 ? (
                <div className="empty-state" data-testid="drawer-empty"><h3>No enrollment history</h3><p>No course enrollments found for this learner.</p></div>
              ) : (
                <div className="table-scroll" style={{ border: '1px solid #e2e8f0', borderRadius: 8 }}>
                  <table data-testid="drawer-history-table">
                    <thead><tr>
                      <th>Platform</th><th>Course</th><th>Status</th><th className="td-right">Progress</th><th>Enrolled</th><th>Completed</th>
                    </tr></thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <tr key={i} data-testid={`drawer-history-row-${i}`}>
                          <td style={{ fontSize: 12 }}>{row.platform || '—'}</td>
                          <td style={{ fontSize: 13 }}>{row.course_name || '—'}</td>
                          <td><StatusPill status={row.enrollment_status} /></td>
                          <td className="td-right" style={{ fontWeight: 600 }}>{row.progress_pct != null ? `${row.progress_pct}%` : '—'}</td>
                          <td style={{ fontSize: 12 }}>{fmtDT(row.enrolled_at)}</td>
                          <td style={{ fontSize: 12 }}>{fmtDT(row.completed_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
